import { colors } from '@dixa/lib-shared-tokens';
import { useHasAccess } from 'lib-shared-access-control';
import { useMemo } from 'react';

import { appActions } from 'app/app.actions';
import { useColorMode } from 'app/utils/colorMode';
import { presenceActions } from 'components/presence/actions/presenceActions';
import { actions as personalActions } from 'components/settings/actions/personal';
import { actions as telephonyActions } from 'components/telephony/actions/telephony';
import { CommandPaletteAction, useCommandPalette } from 'utils/commandPalette';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

const { presenceSetStatus } = presenceActions;

/** Returns quick actions for the command palette. */
export function useQuickActions(): readonly CommandPaletteAction[] {
  const dispatch = useAppDispatch();
  const { createAction, setQuery } = useCommandPalette();
  const { colorMode, isColorModeSupported, toggleColorMode } = useColorMode();

  // Get current presence status
  const currentPresenceStatus = useAppSelector(
    (state) => state.presence.presence.status,
  );

  // Get sidebar state
  const isLeftMenuOpen = useAppSelector(
    (state) => state.settings.personal.isLeftMenuOpen,
  );

  // Feature flags
  const { smsChannel, emailComposeV2, customAwayStatus } = useFeatureFlag([
    'sms-channel',
    'email-compose-v2',
    'custom-away-status',
  ]);

  // Access control
  const hasReplyAccess = useHasAccess('conversation.reply');

  return useMemo(() => {
    const actions: CommandPaletteAction[] = [
      // Search hints - set the query prefix when selected (keepOpen to stay in modal)
      createAction({
        label: 'Search contacts (type @)',
        aliases: ['find users', 'find customers', 'find people'],
        icon: 'team',
        section: 'Quick actions',
        performFn: () => setQuery('@'),
        keepOpen: true,
      }),
      createAction({
        label: 'Find conversation by ID (type #)',
        aliases: ['find ticket', 'lookup conversation', 'goto conversation'],
        icon: 'chat',
        section: 'Quick actions',
        performFn: () => setQuery('#'),
        keepOpen: true,
      }),
      createAction({
        label: 'Run macro (type !)',
        aliases: ['automation', 'trigger', 'macro', 'workflow'],
        icon: 'merge',
        section: 'Quick actions',
        performFn: () => setQuery('!'),
        keepOpen: true,
      }),
    ];

    // Dark mode toggle - only if supported
    if (isColorModeSupported) {
      actions.push(
        createAction({
          label: `Toggle ${colorMode === 'dark' ? 'light' : 'dark'} mode`,
          aliases: ['theme', 'appearance', 'dark mode', 'light mode'],
          icon: colorMode === 'dark' ? 'sun' : 'moon',
          section: 'Quick actions',
          commandName: 'app:toggleColorMode',
          performFn: () => toggleColorMode(),
        }),
      );
    }

    // Toggle left navigation sidebar
    actions.push(
      createAction({
        label: 'Toggle left sidebar',
        aliases: ['left menu', 'left sidebar', 'navigation', 'main menu'],
        icon: 'bars',
        section: 'Quick actions',
        commandName: 'app:toggleLeftSidebar',
        performFn: () => {
          dispatch(
            personalActions.personalLeftMenu({
              key: 'isLeftMenuOpen',
              value: !isLeftMenuOpen,
            }),
          );
        },
      }),
    );

    // Compose Email - requires reply access
    if (hasReplyAccess) {
      actions.push(
        createAction({
          label: 'Compose new email',
          aliases: ['new email', 'write email', 'send email', 'mail'],
          icon: 'mail',
          section: 'Quick actions',
          commandName: 'conversation:composeNewEmail',
          performFn: () => {
            if (emailComposeV2) {
              dispatch(
                appActions.showModal({
                  modal: {
                    type: 'app:composeEmail',
                    arg: {},
                  },
                }),
              );
            } else {
              pubSub.publish('command:conversation:composeNewEmail');
            }
          },
        }),
      );
    }

    // Compose SMS - requires sms-channel feature flag and reply access
    if (smsChannel && hasReplyAccess) {
      actions.push(
        createAction({
          label: 'Compose new SMS',
          aliases: ['text', 'message', 'new sms', 'send sms'],
          icon: 'sms',
          section: 'Quick actions',
          commandName: 'conversation:composeNewSms',
          performFn: () => {
            dispatch(
              appActions.showModal({
                modal: { type: 'app:composeSms' },
              }),
            );
          },
        }),
      );
    }

    // Open phone dropdown
    actions.push(
      createAction({
        label: 'Open dialer',
        aliases: ['call', 'dialer', 'dial', 'telephone', 'phone'],
        icon: 'phone',
        section: 'Quick actions',
        commandName: 'app:openDialer',
        performFn: () => {
          dispatch(telephonyActions.telephonyOpenDropdown());
        },
      }),
    );

    // Presence toggle - when going to "away" with custom away status feature enabled,
    // open the presence dropdown to show away status options
    const isWorking = currentPresenceStatus === 'working';
    actions.push(
      createAction({
        label: isWorking ? 'Set away' : 'Set working',
        aliases: [
          'status',
          'available',
          'away',
          'online',
          'work',
          'busy',
          'presence',
          'toggle',
        ],
        icon: 'circle-filled',
        iconColor: isWorking ? colors.orange['500'] : colors.green['500'],
        section: 'Quick actions',
        commandName: 'app:togglePresence',
        performFn: () => {
          dispatch(
            presenceSetStatus({ status: isWorking ? 'away' : 'working' }),
          );

          // If going to away and custom away status feature is enabled,
          // trigger the presence dropdown to open with away statuses expanded
          if (isWorking && customAwayStatus) {
            pubSub.publish('presenceAwayStatusSet');
          }
        },
      }),
    );

    return actions;
  }, [
    colorMode,
    createAction,
    currentPresenceStatus,
    customAwayStatus,
    dispatch,
    emailComposeV2,
    hasReplyAccess,
    isColorModeSupported,
    isLeftMenuOpen,
    setQuery,
    smsChannel,
    toggleColorMode,
  ]);
}
